import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Tooltip,
} from 'chart.js'
import {Chart} from 'react-chartjs-2'
import {Modal, ModalBody, ModalHeader} from 'react-bootstrap'
import {KTCard, KTCardBody, KTIcon} from '../../../_websoft/helpers'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import TablePagination from '../../components/TablePagination/TablePagination'
import notifyToast from '../../components/Toast/Toast'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import ArtikliGalanterija from '../../components/ArtikliGalanterija/ArtikliGalanterija'
import {PaginationModel} from '../../models/PaginationModel'
import {
    getProductionSurplusStatus,
    PRODUCTION_SURPLUS_STATUS_ALLOCATED,
    PRODUCTION_SURPLUS_STATUS_AVAILABLE,
    PRODUCTION_SURPLUS_STATUS_WRITTEN_OFF,
    PRODUCTION_SURPLUS_STATUSES,
    ProductionSurplusItem,
    ProductionSurplusParams,
} from '../../models/ProductionSurplus'
import {getAll, writeOff} from '../../api/productionSurplus'
import axios from 'axios'
import {getAuth} from '../../modules/auth'
import {UposlenikModel} from '../../models/UposlenikModel'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

type OperacijaOption = {
    Id: number
    Naziv: string
}

const API_URL = process.env.REACT_APP_API_URL
const auth: UposlenikModel = getAuth()!
const headers = {Accept: 'application/json', Authorization: auth?.access_token ?? ''}

const formatDate = (date?: string) => (date ? new Date(date).toLocaleDateString('bs-BA') : '-')

const formatSeconds = (seconds?: number) => {
    const totalSeconds = Number(seconds ?? 0)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const secs = totalSeconds % 60

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

const formatNumber = (value?: number | string) => Number(value ?? 0).toLocaleString('bs-BA')

const getArticleName = (item: ProductionSurplusItem) =>
    item.artikal?.ArtNaziv ??
    item.artikal?.Naziv ??
    item.operacija?.artikal?.ArtNaziv ??
    item.operacija?.artikal?.Naziv ??
    '-'
const getOperationName = (item: ProductionSurplusItem) => item.operacija?.naziv ?? item.operacija?.Naziv ?? '-'
const getEmployeeName = (item: ProductionSurplusItem) => item.uposlenik?.UposlenikImePrezime ?? item.uposlenik?.Naziv ?? '-'

const StatusBadge = ({status}: {status?: string | number}) => {
    const statusInfo = getProductionSurplusStatus(status)
    return <span className={`badge badge-light-${statusInfo.badge}`}>{statusInfo.label}</span>
}

const extractPaginator = (responseData: any, params: ProductionSurplusParams) => {
    const paginator = responseData?.data?.data && Array.isArray(responseData.data.data)
        ? responseData.data
        : responseData
    const rows = paginator?.data ?? []
    const perPage = paginator?.per_page ?? params.per_page
    const total = paginator?.total ?? rows.length

    return {
        rows: Array.isArray(rows) ? rows : [],
        pagination: {
            curPage: paginator?.current_page ?? params.page,
            totalPage: paginator?.last_page ?? Math.max(1, Math.ceil(total / perPage)),
            perPage: perPage,
        },
    }
}

const getChartData = (label: string, rows: {name: string; value: number}[], color: string) => ({
    labels: rows.map((item) => item.name),
    datasets: [
        {
            label,
            data: rows.map((item) => item.value),
            backgroundColor: color,
            borderRadius: 4,
        },
    ],
})

const chartOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false,
        },
    },
    scales: {
        x: {
            beginAtZero: true,
        },
    },
}

const ProductionSurplusPage: React.FC = () => {
    const [data, setData] = useState<ProductionSurplusItem[]>([])
    const [loading, setLoading] = useState(false)
    const [pagination, setPagination] = useState<PaginationModel>({
        curPage: 1,
        totalPage: 1,
        perPage: 10,
    })
    const [params, setParams] = useState<ProductionSurplusParams>({
        page: 1,
        per_page: 10,
    })
    const [filters, setFilters] = useState<Partial<ProductionSurplusParams>>({})
    const [operationOptions, setOperationOptions] = useState<OperacijaOption[]>([])
    const [viewItem, setViewItem] = useState<ProductionSurplusItem | null>(null)
    const [allocateItem, setAllocateItem] = useState<ProductionSurplusItem | null>(null)
    const [writeOffItem, setWriteOffItem] = useState<ProductionSurplusItem | null>(null)
    const [chartsOpen, setChartsOpen] = useState(false)
    const [writeOffNote, setWriteOffNote] = useState('')
    const [writeOffLoading, setWriteOffLoading] = useState(false)

    const loadData = useCallback(() => {
        setLoading(true)
        getAll(params)
            .then((response) => {
                const result = extractPaginator(response.data, params)
                setData(result.rows)
                setPagination(result.pagination)
            })
            .catch((error: any) =>
                notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greška prilikom učitavanja viška proizvodnje.')
            )
            .finally(() => setLoading(false))
    }, [params])

    useEffect(() => {
        loadData()
    }, [loadData])

    const loadOperations = (articleId?: string | number) => {
        setOperationOptions([])
        setFilters((previous) => ({...previous, idOperacije: ''}))

        if (!articleId) {
            return
        }

        axios
            .get(`${API_URL}/operacijeArtikla`, {headers: headers, params: {artikal: articleId}})
            .then((response) => {
                setOperationOptions(response.data.map((item) => ({Id: item.Id, Naziv: item.Naziv})))
            })
            .catch((error: any) =>
                notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greška prilikom učitavanja operacija.')
            )
    }

    const onSearch = () => {
        setParams({
            page: 1,
            per_page: pagination.perPage,
            status: filters.status,
            idArtikla: filters.idArtikla,
            idOperacije: filters.idOperacije,
            datumOd: filters.datumOd,
            datumDo: filters.datumDo,
        })
    }

    const onReset = () => {
        setFilters({})
        setOperationOptions([])
        setParams({page: 1, per_page: pagination.perPage})
    }

    const onWriteOff = () => {
        if (!writeOffItem) {
            return
        }
        if (!writeOffNote.trim()) {
            notifyToast(false, 'Unesite razlog otpisa.')
            return
        }

        setWriteOffLoading(true)
        writeOff(writeOffItem.id, writeOffNote)
            .then((response) => {
                notifyToast(true, response?.message ?? 'Višak proizvodnje je otpisan.')
                setWriteOffItem(null)
                setWriteOffNote('')
                loadData()
            })
            .catch((error: any) => notifyToast(false, error?.response?.data?.message ?? error?.message))
            .finally(() => setWriteOffLoading(false))
    }

    const totals = useMemo(() => {
        return {
            totalQuantity: data.reduce((sum, item) => sum + Number(item.raspolozivaKolicina ?? 0), 0),
            available: data.filter((item) => getProductionSurplusStatus(item.status).value === PRODUCTION_SURPLUS_STATUS_AVAILABLE).length,
            allocated: data.filter((item) => getProductionSurplusStatus(item.status).value === PRODUCTION_SURPLUS_STATUS_ALLOCATED).length,
            writtenOff: data.filter((item) => getProductionSurplusStatus(item.status).value === PRODUCTION_SURPLUS_STATUS_WRITTEN_OFF).length,
        }
    }, [data])

    const articleChartRows = useMemo(() => {
        const grouped: {[key: string]: number} = {}
        data.forEach((item) => {
            const name = getArticleName(item)
            grouped[name] = (grouped[name] ?? 0) + Number(item.raspolozivaKolicina ?? 0)
        })
        return Object.entries(grouped)
            .map(([name, value]) => ({name, value}))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10)
    }, [data])

    const employeeChartRows = useMemo(() => {
        const grouped: {[key: string]: number} = {}
        data.forEach((item) => {
            const name = getEmployeeName(item)
            grouped[name] = (grouped[name] ?? 0) + Number(item.raspolozivaKolicina ?? 0)
        })
        return Object.entries(grouped)
            .map(([name, value]) => ({name, value}))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10)
    }, [data])

    return (
        <KTCard>
            <ToastContainer />
            <Modal show={Boolean(viewItem)} onHide={() => setViewItem(null)} size='lg'>
                <ModalHeader closeButton={true} className='h3'>Detalji viška proizvodnje</ModalHeader>
                <ModalBody>
                    {viewItem && (
                        <div className='row g-5'>
                            <div className='col-md-6'><div className='text-muted fw-semibold'>Artikal</div><div className='fw-bold'>{getArticleName(viewItem)}</div></div>
                            <div className='col-md-6'><div className='text-muted fw-semibold'>Operacija</div><div className='fw-bold'>{getOperationName(viewItem)}</div></div>
                            <div className='col-md-6'><div className='text-muted fw-semibold'>Radnik</div><div className='fw-bold'>{getEmployeeName(viewItem)}</div></div>
                            <div className='col-md-6'><div className='text-muted fw-semibold'>Originalna količina</div><div className='fw-bold'>{formatNumber(viewItem.kolicina)}</div></div>
                            <div className='col-md-6'><div className='text-muted fw-semibold'>Raspoloživa količina</div><div className='fw-bold'>{formatNumber(viewItem.raspolozivaKolicina)}</div></div>
                            <div className='col-md-6'><div className='text-muted fw-semibold'>Vrijeme mašine</div><div className='fw-bold'>{formatSeconds(viewItem.trajanjeSekundi)}</div></div>
                            <div className='col-md-6'><div className='text-muted fw-semibold'>Datum kreiranja</div><div className='fw-bold'>{formatDate(viewItem.datum)}</div></div>
                            <div className='col-12'><div className='text-muted fw-semibold'>Napomena</div><div className='fw-bold'>{viewItem.napomena ?? '-'}</div></div>
                        </div>
                    )}
                </ModalBody>
            </Modal>
            <Modal show={Boolean(allocateItem)} onHide={() => setAllocateItem(null)}>
                <ModalHeader closeButton={true} className='h3'>Alokacija</ModalHeader>
                <ModalBody>
                    <div className='text-muted py-6'>Modul za alokaciju uskoro dostupan.</div>
                </ModalBody>
            </Modal>
            <Modal show={Boolean(writeOffItem)} onHide={() => setWriteOffItem(null)}>
                <ModalHeader closeButton={true} className='h3 bg-warning'>Otpis viška proizvodnje</ModalHeader>
                <ModalBody>
                    <label className='form-label required fw-bold'>Razlog otpisa</label>
                    <textarea
                        className='form-control form-control-solid mb-6'
                        rows={4}
                        value={writeOffNote}
                        onChange={(event) => setWriteOffNote(event.target.value)}
                    />
                    <div className='d-flex justify-content-end gap-3'>
                        <button type='button' className='btn btn-light' onClick={() => setWriteOffItem(null)}>Odustani</button>
                        <button type='button' className='btn btn-danger' disabled={writeOffLoading} onClick={onWriteOff}>Otpiši</button>
                    </div>
                </ModalBody>
            </Modal>
            <Modal show={chartsOpen} onHide={() => setChartsOpen(false)} size='xl' dialogClassName='modal-dialog-scrollable'>
                <ModalHeader closeButton={true} className='h3'>Grafikoni viška proizvodnje</ModalHeader>
                <ModalBody>
                    <div className='row g-5'>
                        <div className='col-12'>
                            <div className='card border'>
                                <div className='card-header'><div className='card-title fw-bold'>Top 10 artikala</div></div>
                                <div className='card-body' style={{height: 360}}>
                                    <Chart type='bar' data={getChartData('Količina', articleChartRows, '#3E97FF')} options={chartOptions} />
                                </div>
                            </div>
                        </div>
                        <div className='col-12'>
                            <div className='card border'>
                                <div className='card-header'><div className='card-title fw-bold'>Top radnici</div></div>
                                <div className='card-body' style={{height: 360}}>
                                    <Chart type='bar' data={getChartData('Količina', employeeChartRows, '#50CD89')} options={chartOptions} />
                                </div>
                            </div>
                        </div>
                    </div>
                </ModalBody>
            </Modal>
            <KTCardBody className='py-4'>
                <div className='d-flex justify-content-between align-items-center mb-8'>
                    <div>
                        <h2 className='mb-1'>Višak proizvodnje</h2>
                        <div className='text-muted fw-semibold'>Pregled i upravljanje viškom proizvodnje.</div>
                    </div>
                    <button type='button' className='btn btn-light-primary' onClick={() => setChartsOpen(true)}>
                        <KTIcon iconName='chart-simple' className='fs-2' />
                        Grafikoni
                    </button>
                </div>

                <div className='row g-5 mb-8'>
                    <div className='col-md-3'><div className='border rounded p-5 h-100'><div className='text-muted fw-semibold'>Ukupna količina viška</div><div className='fs-2 fw-bold'>{formatNumber(totals.totalQuantity)}</div></div></div>
                    <div className='col-md-3'><div className='border rounded p-5 h-100'><div className='text-muted fw-semibold'>Raspoloživi zapisi</div><div className='fs-2 fw-bold'>{totals.available}</div></div></div>
                    <div className='col-md-3'><div className='border rounded p-5 h-100'><div className='text-muted fw-semibold'>Alocirani zapisi</div><div className='fs-2 fw-bold'>{totals.allocated}</div></div></div>
                    <div className='col-md-3'><div className='border rounded p-5 h-100'><div className='text-muted fw-semibold'>Otpisano</div><div className='fs-2 fw-bold'>{totals.writtenOff}</div></div></div>
                </div>

                <div className='row g-5 mb-8'>
                    <div className='col-lg-2'>
                        <label className='form-label fw-bold'>Datum od</label>
                        <input type='date' className='form-control form-control-solid' value={filters.datumOd ?? ''} onChange={(event) => setFilters({...filters, datumOd: event.target.value})} />
                    </div>
                    <div className='col-lg-2'>
                        <label className='form-label fw-bold'>Datum do</label>
                        <input type='date' className='form-control form-control-solid' value={filters.datumDo ?? ''} onChange={(event) => setFilters({...filters, datumDo: event.target.value})} />
                    </div>
                    <div className='col-lg-2'>
                        <label className='form-label fw-bold'>Status</label>
                        <select className='form-select form-select-solid' value={filters.status ?? ''} onChange={(event) => setFilters({...filters, status: event.target.value})}>
                            <option value=''>Svi</option>
                            {PRODUCTION_SURPLUS_STATUSES.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                        </select>
                    </div>
                    <div className='col-lg-3'>
                        <label className='form-label fw-bold'>Artikal</label>
                        <ArtikliGalanterija
                            value={filters.idArtikla}
                            selectName='idArtikla'
                            onChange={(name, value) => {
                                setFilters({...filters, idArtikla: value, idOperacije: ''})
                                loadOperations(value)
                            }}
                        />
                    </div>
                    <div className='col-lg-3'>
                        <label className='form-label fw-bold'>Operacija</label>
                        <select className='form-select form-select-solid' value={filters.idOperacije ?? ''} onChange={(event) => setFilters({...filters, idOperacije: event.target.value})}>
                            <option value=''>Sve operacije</option>
                            {operationOptions.map((operation) => <option key={operation.Id} value={operation.Id}>{operation.Naziv}</option>)}
                        </select>
                    </div>
                    <div className='col-12 d-flex justify-content-end gap-3'>
                        <button type='button' className='btn btn-light' onClick={onReset}>Resetuj</button>
                        <button type='button' className='btn btn-primary' onClick={onSearch}>Pretraži</button>
                    </div>
                </div>

                {loading ? (
                    <LoadingComponent />
                ) : data.length === 0 ? (
                    <div className='text-center text-muted py-15'>
                        <KTIcon iconName='abstract-26' className='fs-5x text-gray-400 mb-4' />
                        <div className='fw-bold fs-4'>Nije pronađen višak proizvodnje.</div>
                    </div>
                ) : (
                    <div className='table-responsive'>
                        <table className='table align-middle table-bordered gy-5 no-footer'>
                            <thead>
                                <tr className='text-start fw-bolder fs-7 text-uppercase gs-0 bg-dark p-2'>
                                    <th className='text-white'>ID</th>
                                    <th className='text-white'>Datum</th>
                                    <th className='text-white'>Artikal</th>
                                    <th className='text-white'>Operacija</th>
                                    <th className='text-white'>Radnik</th>
                                    <th className='text-white'>Količina</th>
                                    <th className='text-white'>Raspoloživa količina</th>
                                    <th className='text-white'>Vrijeme mašine</th>
                                    <th className='text-white'>Status</th>
                                    <th className='text-white'>Akcije</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((item) => (
                                    <tr key={item.id}>
                                        <td>{item.id}</td>
                                        <td>{formatDate(item.datum)}</td>
                                        <td>{getArticleName(item)}</td>
                                        <td>{getOperationName(item)}</td>
                                        <td>{getEmployeeName(item)}</td>
                                        <td>{formatNumber(item.kolicina)}</td>
                                        <td>{formatNumber(item.raspolozivaKolicina)}</td>
                                        <td>{formatSeconds(item.trajanjeSekundi)}</td>
                                        <td><StatusBadge status={item.status} /></td>
                                        <td>
                                            <button type='button' className='btn btn-sm btn-light-primary me-2' onClick={() => setViewItem(item)}>Pregled</button>
                                            <button type='button' className='btn btn-sm btn-light-info me-2' onClick={() => setAllocateItem(item)}>Alociraj</button>
                                            <button type='button' className='btn btn-sm btn-light-danger' onClick={() => setWriteOffItem(item)}>Otpiši</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <TablePagination
                    updatePage={(page, per_page) => setParams({...params, page: page, per_page: per_page})}
                    pagination={pagination}
                    isLoading={loading}
                />
            </KTCardBody>
        </KTCard>
    )
}

export default ProductionSurplusPage
